import Link from 'next/link';

import { ComponentPropsWithoutRef } from 'react';

import classNames from 'classnames';
import { format, isPast, isValid } from 'date-fns';

import { Spinner } from '@uikit';

import { LAUNCHPAD_SALE_POOL_STATUS, LaunchpadSalePool } from '@api';

import { MIXPANEL_EVENT_NAMES } from '@shared/api/mixPanel';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { useMixPanel } from '@shared/common/providers/MixPanelProvider';
import { LAUNCHPAD_INTERNAL_ROUTES } from '@shared/constants';

import { useAuthModal } from '@launchpad/components/ui/buttons/ConnectWalletButton';

import { getIsSaleCompletedPoolStatus } from './PoolCard';

const isDateInPast = (startDate?: string) => {
  if (!startDate || !isValid(new Date(startDate))) {
    return false;
  }

  return isPast(new Date(startDate));
};

interface DepositButtonProps {
  projectSlug: string;
  pool: LaunchpadSalePool;
  userDeposit: number;
  className?: string;
  onJoinWhitelist?: () => void;
  title?: string;
}

const BaseDepositButton = ({
  disabled,
  className,
  ...props
}: ComponentPropsWithoutRef<'button'>) => {
  return (
    <button
      disabled={disabled}
      className={classNames(
        'ui-button ui-button-solid-primary',
        {
          'bg-neutral-300 text-neutral-50 cursor-not-allowed': disabled,
        },
        className,
      )}
      {...props}
    />
  );
};

const DepositButton = ({
  pool,
  projectSlug,
  userDeposit,
  className,
  onJoinWhitelist,
}: DepositButtonProps) => {
  const { login } = useAuthModal();
  const { isAuth, isLoading: isAuthLoading, user } = useAuth();
  const { track } = useMixPanel();

  const trackJoinWhiteListClick = () => {
    if (user?.id) {
      track(MIXPANEL_EVENT_NAMES.LAUNCHPAD_WHITELIST_REQUIREMENTS_VIEWED, {
        distinct_id: user.id,
        project: pool.attributes.name,
      });
    }
  };

  if (getIsSaleCompletedPoolStatus(pool.attributes.status)) {
    return (
      <Link
        onClick={(event) => {
          if (!isAuth) {
            event.stopPropagation();
            event.preventDefault();

            return login();
          }
        }}
        href={isAuth ? LAUNCHPAD_INTERNAL_ROUTES.sale(projectSlug, pool.id) : ''}
        className={classNames(className, 'ui-button bg-neutral-200 text-neutral-50')}
      >
        Sale Completed
      </Link>
    );
  }

  // TODO: check why participate button shown first for 1s then it changes to deposit button

  if (isAuthLoading) {
    return (
      <BaseDepositButton disabled className={className}>
        <Spinner size="sm" color="disabled" />
      </BaseDepositButton>
    );
  }

  const isWhitelistRequired =
    pool.attributes?.meta?.whiteListExists && !pool.attributes?.meta?.isWhitelistParticipant;

  if (isWhitelistRequired) {
    const isWhitelistStarted = isDateInPast(pool?.whiteList?.attributes.startDate);
    const isWhitelistEnded = isDateInPast(pool?.whiteList?.attributes.endDate);

    if (!isWhitelistStarted) {
      const formattedStartDate =
        pool?.whiteList?.attributes.startDate &&
        isValid(new Date(pool.whiteList.attributes.startDate))
          ? format(new Date(pool.whiteList.attributes.startDate), 'dd.MM.yy, HH:mm')
          : 'TBA';

      return (
        <BaseDepositButton disabled className={className}>
          Whitelist starts {formattedStartDate}
        </BaseDepositButton>
      );
    } else if (isWhitelistEnded) {
      return (
        <BaseDepositButton disabled className={className}>
          Whitelist Closed
        </BaseDepositButton>
      );
    } else {
      return (
        <BaseDepositButton
          onClick={(event) => {
            if (!isAuth) {
              event.stopPropagation();
              event.preventDefault();

              return login();
            }

            trackJoinWhiteListClick();
            return onJoinWhitelist?.();
          }}
          className={className}
        >
          Join Whitelist
        </BaseDepositButton>
      );
    }
  }

  if (pool.attributes.status === LAUNCHPAD_SALE_POOL_STATUS.ANNOUNCED) {
    const label =
      pool.attributes.announcement ||
      `Sale starts ${format(new Date(pool.attributes.startDate), 'dd.MM.yy, HH:mm')}`;

    return (
      <BaseDepositButton
        title={pool.attributes.announcement || undefined}
        disabled
        className={className}
      >
        <span className="truncate">{label}</span>
      </BaseDepositButton>
    );
  }

  return (
    <Link
      onClick={(event) => {
        if (!isAuth) {
          event.stopPropagation();
          event.preventDefault();

          return login();
        }
      }}
      href={isAuth ? LAUNCHPAD_INTERNAL_ROUTES.sale(projectSlug, pool.id) : ''}
      className={classNames('ui-button ui-button-solid-primary', className)}
    >
      {userDeposit > 0 ? 'Deposit More' : 'Participate Now'}
    </Link>
  );
};

export default DepositButton;
